/**
 * Per-user project entitlement (app_access.allowed_projects, migration 00541).
 *
 *   projectIds null      -> no restriction (admins, and every account approved
 *                           before 00541)
 *   projectIds []        -> approved, but no projects assigned yet
 *   projectIds [ids...]  -> only those projects
 *
 * <AccessGate> sets this before the app renders (with the portfolios that
 * hold the allowed projects), and supabaseService.queryTable / queryOne read
 * it to scope every projects, portfolios, wbs_elements, tasks and risks read.
 * Session-scoped, not persisted: re-read from app_access on each sign-in.
 */
import { create } from 'zustand'

export interface ProjectEntitlement {
  /** null = unrestricted. Includes the read-only agile projects below. */
  projectIds: string[] | null
  /** Portfolios that hold the allowed projects (so the tree can name them). */
  portfolioIds: string[] | null
  /**
   * Projects whose agile pages are read-only for this user
   * (app_access.agile_read_only_projects, migration 00582). [] = none.
   */
  agileReadOnlyIds?: string[]
  /**
   * The read-only agile projects this user sees ONLY through that grant (not
   * in allowed_projects). They are kept on the agile pages of those projects.
   */
  agileOnlyIds?: string[]
}

const UNRESTRICTED: ProjectEntitlement = { projectIds: null, portfolioIds: null, agileReadOnlyIds: [], agileOnlyIds: [] }

interface ProjectEntitlementState extends Required<ProjectEntitlement> {
  setProjectEntitlement: (e: ProjectEntitlement) => void
}

export const useProjectEntitlementStore = create<ProjectEntitlementState>((set) => ({
  ...(UNRESTRICTED as Required<ProjectEntitlement>),
  setProjectEntitlement: (e) =>
    set({
      projectIds: e.projectIds,
      portfolioIds: e.portfolioIds,
      agileReadOnlyIds: e.agileReadOnlyIds ?? [],
      agileOnlyIds: e.agileOnlyIds ?? [],
    }),
}))

/** The live entitlement, for non-React callers (the data layer). */
export function currentProjectEntitlement(): ProjectEntitlement {
  const s = useProjectEntitlementStore.getState()
  return { projectIds: s.projectIds, portfolioIds: s.portfolioIds, agileReadOnlyIds: s.agileReadOnlyIds, agileOnlyIds: s.agileOnlyIds }
}

export function useProjectEntitlement(): ProjectEntitlement {
  const projectIds = useProjectEntitlementStore((s) => s.projectIds)
  const portfolioIds = useProjectEntitlementStore((s) => s.portfolioIds)
  return { projectIds, portfolioIds }
}

/**
 * Combine the two app_access lists into the session entitlement. A read-only
 * agile grant also makes its project visible; for a restricted user, the
 * projects that come in only through that grant are "agile only".
 * Admins are never restricted or read-only.
 */
export function resolveProjectAccess(input: {
  isAdmin: boolean
  allowedProjects: string[] | null
  agileReadOnlyProjects: string[] | null
}): { projectIds: string[] | null; agileReadOnlyIds: string[]; agileOnlyIds: string[] } {
  if (input.isAdmin) return { projectIds: null, agileReadOnlyIds: [], agileOnlyIds: [] }
  const agile = input.agileReadOnlyProjects ?? []
  if (input.allowedProjects === null) return { projectIds: null, agileReadOnlyIds: agile, agileOnlyIds: [] }
  const allowed = new Set(input.allowedProjects)
  return {
    projectIds: [...new Set([...input.allowedProjects, ...agile])],
    agileReadOnlyIds: agile,
    agileOnlyIds: agile.filter((id) => !allowed.has(id)),
  }
}

/** Route prefixes a user keeps on a project they see only through a read-only agile grant. */
export const AGILE_READ_ONLY_PATHS = ['/backlog', '/board', '/sprints', '/roadmap', '/agile-insights'] as const

export function isAgileReadOnlyPath(pathname: string): boolean {
  return AGILE_READ_ONLY_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

const PERSONAL_PATHS = ['/dashboard', '/my-work', '/my-profile']

/**
 * Where <AgileOnlyRouteGuard> sends this path, or null to let it through. A
 * project seen only through a read-only agile grant stays on its agile pages;
 * personal pages and the project list stay open.
 */
export function resolveAgileOnlyRedirect(pathname: string, selectedProjectId: string | null, agileOnlyIds: string[]): string | null {
  if (agileOnlyIds.length === 0) return null
  const detail = /^\/projects\/([^/]+)/.exec(pathname)
  if (detail && detail[1] !== 'new' && agileOnlyIds.includes(decodeURIComponent(detail[1]!))) return '/backlog'
  if (!selectedProjectId || !agileOnlyIds.includes(selectedProjectId)) return null
  if (pathname === '/' || pathname === '/projects' || isAgileReadOnlyPath(pathname)) return null
  if (PERSONAL_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null
  return '/backlog'
}

export function isProjectRestricted(e: ProjectEntitlement): boolean {
  return e.projectIds !== null
}

/**
 * Whether this person may switch into a demo profile or another environment.
 * A demo profile signs the browser in to a SHARED demo account, which is
 * approved and unrestricted: a project-restricted user would step around their
 * project list, and their test results would be stamped with the demo
 * account's tester name. So a restricted user stays in their own account.
 */
export function mayLeaveOwnAccount(e: ProjectEntitlement = currentProjectEntitlement()): boolean {
  return e.projectIds === null
}

/** Read the column; anything that is not an array of strings is "no restriction". */
export function normalizeAllowedProjects(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null
  return [...new Set(v.filter((x): x is string => typeof x === 'string' && x.length > 0))]
}

/** Never matches a real id: PostgREST's in.() needs at least one value. */
export const NO_MATCH_ID = '__no_project_access__'

/**
 * How a restricted user's read of `table` is scoped: the column to filter and
 * the ids it may hold. null = this table is not project-scoped (or the user is
 * unrestricted), so the normal organization scope applies.
 */
export function projectScopeFor(
  table: string,
  e: ProjectEntitlement,
): { column: string; ids: string[] } | null {
  if (e.projectIds === null) return null
  const pad = (ids: string[]) => (ids.length ? ids : [NO_MATCH_ID])
  switch (table) {
    case 'projects': return { column: 'id', ids: pad(e.projectIds) }
    case 'wbs_elements':
    case 'tasks':
    case 'risks': return { column: 'project_id', ids: pad(e.projectIds) }
    case 'portfolios': return { column: 'id', ids: pad(e.portfolioIds ?? []) }
    default: return null
  }
}

/** May a restricted user open this one row? (true when unrestricted or not project-scoped). */
export function mayReadRow(table: string, id: string, e: ProjectEntitlement): boolean {
  const scope = projectScopeFor(table, e)
  if (!scope || scope.column !== 'id') return true
  return scope.ids.includes(id)
}
